// =============================================================================
// SHOPPING BAG SERVICE
// =============================================================================
// Service for managing shopping bag state with sessionStorage persistence

import type { ShoppingBag, ShoppingBagItem } from '@/types/shoppingBag';
import { sessionStorageUtils } from '@/utils';

const STORAGE_KEY = 'awani_shopping_bag';
const VERSION = '1.0.0';

/**
 * Shopping Bag Service
 * Manages shopping bag state with sessionStorage persistence
 */
export class ShoppingBagService {
  private bag: ShoppingBag;
  private listeners: Set<(bag: ShoppingBag) => void>;

  constructor() {
    this.bag = this.loadFromStorage();
    this.listeners = new Set();
  }

  /**
   * Load bag from sessionStorage
   */
  private loadFromStorage(): ShoppingBag {
    try {
      const stored = sessionStorageUtils.get<{ bag: ShoppingBag; version: string }>(STORAGE_KEY);
      
      if (stored && stored.version === VERSION && stored.bag) {
        return stored.bag;
      }
      
      // If version mismatch or invalid data, return empty bag
      return this.getEmptyBag();
    } catch (error) {
      // Error loading shopping bag from storage
      return this.getEmptyBag();
    }
  }

  /**
   * Save bag to sessionStorage
   */
  private saveToStorage(): void {
    try {
      sessionStorageUtils.set(STORAGE_KEY, {
        bag: this.bag,
        version: VERSION,
      });
    } catch (error) {
      // Error saving shopping bag to storage
    }
  }

  /**
   * Get empty bag structure
   */
  private getEmptyBag(): ShoppingBag {
    return {
      items: [],
      totalItems: 0,
      totalAmount: 0,
      totalAmountDisplay: '$0',
      updatedAt: Date.now(),
    };
  }

  /**
   * Calculate totals from items
   */
  private calculateTotals(items: ShoppingBagItem[]): {
    totalItems: number;
    totalAmount: number;
    totalAmountDisplay: string;
  } {
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const totalAmountDisplay = `$${(totalAmount / 100).toFixed(2)}`;

    return { totalItems, totalAmount, totalAmountDisplay };
  }

  /**
   * Generate unique ID for item
   */
  private generateItemId(): string {
    return `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Format price from display string to cents
   */
  private parsePrice(priceDisplay: string): number {
    // Remove $ and parse to cents
    const cleaned = priceDisplay.replace(/[^0-9.]/g, '');
    const dollars = parseFloat(cleaned);
    return Math.round(dollars * 100);
  }

  /**
   * Notify listeners of bag changes
   */
  private notifyListeners(): void {
    this.saveToStorage();
    this.listeners.forEach((listener) => listener(this.bag));
  }

  /**
   * Subscribe to bag changes
   */
  subscribe(listener: (bag: ShoppingBag) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get current bag
   */
  getBag(): ShoppingBag {
    return { ...this.bag };
  }

  /**
   * Add item to bag
   */
  addItem(itemData: Omit<ShoppingBagItem, 'id' | 'quantity' | 'addedAt'>): void {
    // Check if identical item already exists (same product, size)
    const existingIndex = this.bag.items.findIndex(
      (item) =>
        item.categoryName === itemData.categoryName &&
        item.productName === itemData.productName &&
        item.size === itemData.size &&
        item.price === itemData.price
    );

    if (existingIndex !== -1) {
      // Update quantity of existing item
      this.bag.items[existingIndex].quantity += 1;
    } else {
      // Add new item
      const newItem: ShoppingBagItem = {
        ...itemData,
        id: this.generateItemId(),
        quantity: 1,
        addedAt: Date.now(),
      };
      this.bag.items.push(newItem);
    }

    // Recalculate totals
    const totals = this.calculateTotals(this.bag.items);
    this.bag.totalItems = totals.totalItems;
    this.bag.totalAmount = totals.totalAmount;
    this.bag.totalAmountDisplay = totals.totalAmountDisplay;
    this.bag.updatedAt = Date.now();

    this.notifyListeners();
  }

  /**
   * Remove item from bag
   */
  removeItem(itemId: string): void {
    this.bag.items = this.bag.items.filter((item) => item.id !== itemId);

    // Recalculate totals
    const totals = this.calculateTotals(this.bag.items);
    this.bag.totalItems = totals.totalItems;
    this.bag.totalAmount = totals.totalAmount;
    this.bag.totalAmountDisplay = totals.totalAmountDisplay;
    this.bag.updatedAt = Date.now();

    this.notifyListeners();
  }

  /**
   * Update item quantity
   */
  updateQuantity(itemId: string, quantity: number): void {
    if (quantity <= 0) {
      this.removeItem(itemId);
      return;
    }

    const item = this.bag.items.find((item) => item.id === itemId);
    if (item) {
      item.quantity = quantity;

      // Recalculate totals
      const totals = this.calculateTotals(this.bag.items);
      this.bag.totalItems = totals.totalItems;
      this.bag.totalAmount = totals.totalAmount;
      this.bag.totalAmountDisplay = totals.totalAmountDisplay;
      this.bag.updatedAt = Date.now();

      this.notifyListeners();
    }
  }

  /**
   * Clear all items from bag
   */
  clearBag(): void {
    this.bag = this.getEmptyBag();
    this.notifyListeners();
  }

  /**
   * Get bag item count for display
   */
  getItemCount(): number {
    return this.bag.totalItems;
  }
}

// Export singleton instance
export const shoppingBagService = new ShoppingBagService();
export default shoppingBagService;

